import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import {
  resetAccessConfigApiV1AdminAccessConfigUserTypeDelete,
  setAccessConfigApiV1AdminAccessConfigUserTypePut,
} from "@/api/generated/sdk.gen";
import type { AccessConfigRowSchema } from "@/api/generated/types.gen";

import { CLIENT_KEYS } from "@constants/clientKeys";

const ROWS_KEY = [CLIENT_KEYS.ACCESS_CONFIG, "rows"];

const errText = (error: unknown): string =>
  (error as { detail?: string })?.detail || "Request failed.";

/**
 * Replace the row for the returned user type in the cached list, so the matrix
 * reflects the server's authoritative effective set immediately — no refetch
 * gap where the switches read stale data (and no flicker). The endpoints return
 * the full effective row (capabilities + is_default) for exactly this.
 */
const writeRowToCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  row: AccessConfigRowSchema | undefined
) => {
  if (!row) return;
  queryClient.setQueryData<AccessConfigRowSchema[]>(ROWS_KEY, (old) =>
    old ? old.map((r) => (r.user_type === row.user_type ? row : r)) : old
  );
};

/** Set a user type's enabled capabilities (replaces the whole set). */
export const useSetAccessConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userType,
      capabilities,
    }: {
      userType: string;
      capabilities: string[];
    }) => {
      const response = await setAccessConfigApiV1AdminAccessConfigUserTypePut({
        client: apiClient,
        path: { user_type: userType },
        body: { capabilities },
      });
      if ("error" in response && response.error) {
        throw new Error(errText(response.error));
      }
      return response.data;
    },
    onSuccess: (data) => writeRowToCache(queryClient, data),
  });
};

/** Reset a user type to the hardcoded default (delete its config row). */
export const useResetAccessConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userType }: { userType: string }) => {
      const response = await resetAccessConfigApiV1AdminAccessConfigUserTypeDelete({
        client: apiClient,
        path: { user_type: userType },
      });
      if ("error" in response && response.error) {
        throw new Error(errText(response.error));
      }
      return response.data;
    },
    onSuccess: (data) => writeRowToCache(queryClient, data),
  });
};
